import { Stack } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { ChevronLeftIcon } from '@/components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';

import { Button, ButtonProps } from './Button';
import { getColorsForVariant } from './styles';
import { ButtonVariant } from './types';

const useStyles = makeStyles<{ variant: ButtonVariant }>()(
  (theme, { variant }) => ({
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      // Square off the inner corners and drop the second button's left border so
      // the pair reads as one control with a single divider between them.
      '& > *:first-of-type': {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
      '& > *:last-of-type': {
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        borderLeft: 'none',
      },
      // Keep the enabled border color when a half is disabled, so only its
      // label and chevron grey out and the outline of the pair stays whole.
      // Same treatment as `UndoRedoButtonGroup`.
      '& > *:disabled': {
        borderColor: getColorsForVariant(variant, theme).regular.borderColor,
      },
    },
  })
);

export type PrevNextButtonGroupProps = Omit<
  ButtonProps,
  // The group owns these. Each button gets its own handler, its text comes
  // from `previousLabel` / `nextLabel`, and the chevrons are the whole point
  // of the control — letting a caller swap one out would leave a pair that
  // no longer reads as previous and next.
  'onClick' | 'children' | 'startIcon' | 'endIcon'
> & {
  onPrevious: () => void;
  onNext: () => void;
  previousLabel?: string;
  nextLabel?: string;
  /** Disables only the previous button, e.g. on the first item. */
  isPreviousDisabled?: boolean;
  /** Disables only the next button, e.g. on the last item. */
  isNextDisabled?: boolean;
};

/**
 * A paired previous/next control, rendered as a single segmented button.
 *
 * Every remaining `Button` prop is forwarded to both buttons unchanged, so the
 * pair always share a variant, size and state. `disabled` applies to both —
 * `isPreviousDisabled` and `isNextDisabled` narrow it to one side.
 */
export function PrevNextButtonGroup({
  onPrevious,
  onNext,
  previousLabel = 'Prev',
  nextLabel = 'Next',
  isPreviousDisabled = false,
  isNextDisabled = false,
  disabled = false,
  ...buttonProps
}: PrevNextButtonGroupProps) {
  const { classes } = useStyles({ variant: buttonProps.variant });

  return (
    // `direction` as well as the class's `flexDirection`: the class alone
    // would be relying on beating MUI's own Stack styles.
    <Stack direction="row" className={classes.root}>
      {/* Forwarded props come first; everything the group owns is applied
          after, so a caller can't displace it. */}
      <Button
        {...buttonProps}
        startIcon={ChevronLeftIcon}
        disabled={disabled || isPreviousDisabled}
        onClick={onPrevious}
      >
        {previousLabel}
      </Button>
      <Button
        {...buttonProps}
        endIcon={ChevronRightIcon}
        disabled={disabled || isNextDisabled}
        onClick={onNext}
      >
        {nextLabel}
      </Button>
    </Stack>
  );
}
